import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Checkbox, DatePicker, Divider, Modal, Skeleton, Space, Tabs, Typography } from "antd";
import { useAuthenticatedRequest, useAuthenticatedRequestCreator } from "hooks/useRequests";
import { ActiveCard } from "models/cards";
import { EndListingQueueStatus, StaffPackage } from "models/packages";
import { DefaultTimeZone, DisplayDateFormat, DisplayTimeFormat } from "models/websiteSettings";
import moment from "moment";
import { useState } from "react";
import { DefaultPageRequest } from "util/getDefaultPageRequest";
import EndListingQueueHistory, { useQueueHistory } from "./EndListingQueueHistory";

const { Title } = Typography;

const ActiveCardsTab = "1"
const HistoryTab = "2"

const useActiveCards = (enabled: boolean, packageId: string) => {
    const fetch = useAuthenticatedRequest<ActiveCard[]>({
        method: "get",
        url: `/cards/active/${packageId}`
    });

    return useQuery(["cards", "active", packageId], fetch, {
        enabled
    });
}

const useBulkEndCards = () => {
    const submit = useAuthenticatedRequestCreator<void, { packageId: string, relist: boolean, relistDateTime: moment.Moment | null }>(
        ({ packageId, relist, relistDateTime }) => ({
            method: "post",
            url: `/cards/active/${packageId}/relist`,
            data: {
                packageId,
                relist,
                relistStartTime: relist
                    ? relistDateTime?.clone()?.tz(DefaultTimeZone, true)?.utc()
                    : null
            }
        })
    );

    const { mutateAsync: endListings, isLoading: isSubmitting } = useMutation(submit);

    return {
        endListings, isSubmitting
    };
}

const Highlight = (props: { count: number, color?: string }) =>
    <div style={{ display: 'inline', color: props.color ?? 'var(--dcs-bright-blue)' }}>{props.count.toLocaleString()}</div>;

const Summary = (props: {
    total: number,
    endingSoon: number,
    canEnd: number,
    onRelistToggle: (value: boolean) => void
}) => {
    if (props.total === 0 || props.canEnd === 0) {
        return <Title level={4}>There are no active listings available to be bulk ended for this package.</Title>
    }

    if (props.endingSoon === 0) {
        return <Title level={4}>
            There are <Highlight count={props.total} /> active listings in this package.
            <br /><br />
            End all <Highlight count={props.canEnd} color='var(--dcs-dark-green)' /> listings?
        </Title>
    }

    return <Title level={4}>
        There are <Highlight count={props.total} /> active listings in this package,
        but <Highlight count={props.endingSoon} color='var(--dcs-error-red)' /> are ending within 12 hours and
        cannot be ended by this feature.
        <br /><br />
        End the remaining <Highlight count={props.canEnd} color='var(--dcs-dark-green)' /> listings?
    </Title>;
}

// 9 PM
const DefaultScheduledTime = moment().set({ 'hour': 21, minute: 0, second: 0 });

const EndListingsModal = (props: {
    open: boolean;
    package: StaffPackage;
    onClose: () => Promise<void>;
}) => {
    const { data = [], isLoading } = useActiveCards(props.open, props.package.id);
    const { endListings, isSubmitting } = useBulkEndCards();
    const { data: queue = { totalRecords: 0 }, isLoading: isCheckingQueue } = useQueueHistory(props.open, props.package.id, {
        ...DefaultPageRequest, filterBy: { "Status": [EndListingQueueStatus.Queued] }
    });
    const [relist, setRelist] = useState(false);
    const [scheduledTime, setScheduledTime] = useState(DefaultScheduledTime);
    const [tab, setTab] = useState(ActiveCardsTab);
    const [success, setSuccess] = useState(false);

    const endingSoon = data.filter(x => x.hoursBeforeEnding <= 12).length;
    const canEnd = data.length - endingSoon;
    const showRelistOption = canEnd > 0;
    const working = isLoading || isCheckingQueue || isSubmitting;
    const hasActiveQueue = queue.totalRecords > 0;

    const close = async () => {
        await props.onClose();
        setRelist(false);
        setScheduledTime(DefaultScheduledTime);
        setSuccess(false);
        setTab(ActiveCardsTab);
    }

    const submit = async () => {
        await endListings({
            packageId: props.package.id,
            relist,
            relistDateTime: scheduledTime
        });
        setSuccess(true);
    }

    return <Modal
        title={`Bulk End Listings for Package #${props.package.friendlyId}`}
        bodyStyle={{ padding: '0 24px 24px 24px' }}
        width={tab === ActiveCardsTab ? undefined : "1200px"}
        closable={false}
        keyboard={false}
        maskClosable={false}
        destroyOnClose={true}
        open={props.open}
        confirmLoading={working}
        onCancel={async () => await close()}
        cancelButtonProps={{ disabled: isSubmitting }}
        cancelText={success || tab === HistoryTab ? "Close" : "Cancel"}
        onOk={async () => await submit()}
        okButtonProps={{ disabled: data.length === 0 || canEnd === 0 || success, hidden: tab !== ActiveCardsTab }}
        okText={relist ? "End & Relist Items" : "End Items"}
    >
        <Tabs
            activeKey={tab}
            onChange={setTab}
            items={[
                {
                    label: "Active Cards",
                    key: ActiveCardsTab,
                    children:
                        <>
                            {!working && <>
                                <Summary
                                    total={data.length}
                                    endingSoon={endingSoon}
                                    canEnd={canEnd}
                                    onRelistToggle={setRelist}
                                />
                                <Space direction="vertical">
                                    {hasActiveQueue && <Alert type="info" message="The above summary is subject to change, as listings for this package are currently being ended." />}

                                    {success && <Alert type="success" message="Listings have been queued up for processing." />}
                                </Space>
                            </>}

                            {showRelistOption && <>
                                <Divider plain>Options</Divider>
                                <Checkbox
                                    onChange={(e) => setRelist(e.target.checked)}>
                                    Relist ended items, starting at
                                </Checkbox>
                                <DatePicker
                                    format={`${DisplayDateFormat}  ${DisplayTimeFormat}`}
                                    value={scheduledTime}
                                    showTime={{ defaultValue: moment('21:00:00', 'HH:mm:ss') }}
                                    disabledDate={(date: moment.Moment) =>
                                        date.isBefore(moment().subtract(1, 'days')) || date.isAfter(moment().add(3, 'weeks'))}
                                    disabled={!relist}
                                    allowClear={false}
                                    onChange={(dt) => setScheduledTime(dt ?? DefaultScheduledTime)}
                                />
                            </>}

                            {working && <Skeleton active />}
                        </>
                },
                {
                    label: "Queue History",
                    key: HistoryTab,
                    children: <EndListingQueueHistory
                        packageId={props.package.id}
                    />
                }
            ]}
        />

    </Modal>
}

export default EndListingsModal;